import { useState } from 'react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'

// Demo trace shown in the marketing hero card. Shared by the desktop and
// mobile layouts below so the two can't drift apart.
const DEMO_DATA = [
  { time: 'Jan 24', temp: 3.5, odo: 22 },
  { time: 'Jan 25', temp: 3.8, odo: 27 },
  { time: 'Jan 26', temp: 2.9, odo: 19 },
  { time: 'Jan 27', temp: 2.1, odo: 15 },
  { time: 'Jan 28', temp: 1.8, odo: 13 },
  { time: 'Jan 29', temp: 2.0, odo: 15 },
  { time: 'Jan 30', temp: 2.3, odo: 18 },
]

// Hero slides. The XD mobile frame shows left/right arrows over the image,
// which implies a rotating set of featured applications — but only this one
// slide's copy and artwork exist today, and no XD frame defines the others.
// So the markup is slide-driven and the arrows/dots render ONLY when a second
// slide exists: adding entries here turns the carousel on, and until then the
// hero shows no controls that would do nothing when tapped.
const HERO_SLIDES = [
  {
    title: 'Flood Warning System',
    image: '/images/banner.png',
    paragraphs: [
      'Flash flooding often leaves only minutes to react before conditions approach dangerous levels. Flood warning systems save lives, protect property and limit damage to infrastructure by quickly alerting to heavy precipitation and changing water levels.',
      'An effective flood warning system enables authorities to take appropriate action, such as evacuation and control of dams in advance of a flood event, often leading to much less catastrophic outcomes when severe weather strikes.',
    ],
  },
]

// The hero's chart card. One definition, two sizes — the mobile card is
// shorter and drops the axis tick labels, which are illegible at 390px.
function HeroChart({ height, compact = false }) {
  return (
    <div className="bg-white rounded-lg p-3 shadow-lg">
      {/* Written as whole strings rather than an interpolated tail so the
          desktop class list stays in its original order — that keeps the
          rendered desktop markup byte-identical to the pre-split version. */}
      <h2 className={compact ? 'font-bold text-sm mb-2' : 'font-bold text-lg mb-2'}>Flood Warning System</h2>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={DEMO_DATA} margin={{ top: 20, right: compact ? 6 : 30, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" />
          {/* Tick/width overrides are applied ONLY in compact mode. Passing
              undefined lets Recharts fall back to its own defaults, so the
              desktop chart renders exactly as it did before this split. */}
          <XAxis dataKey="time" tick={compact ? { fontSize: 9 } : undefined} />
          {/* Explicit axis widths in BOTH modes. Leaving the desktop width
              `undefined` makes Recharts auto-measure the tick-label text, which
              intermittently returns NaN in this hero layout (negative-margin
              float) — that NaN cascades to the plot width and the whole chart
              renders blank. Fixed widths avoid the measurement entirely. */}
          <YAxis yAxisId="left" domain={[1, 4]} tickFormatter={v => `${v}°C`}
                 width={compact ? 38 : 52} tick={compact ? { fontSize: 9 } : undefined} />
          <YAxis yAxisId="right" orientation="right" domain={[12, 28]} tickFormatter={v => `${v} mg/L`}
                 width={compact ? 46 : 62} tick={compact ? { fontSize: 9 } : undefined} />
          <Tooltip />
          <Legend wrapperStyle={compact ? { fontSize: 10 } : undefined} />
          <Line yAxisId="left" type="monotone" dataKey="temp" name="YSI Temperature (°C)" stroke="#20c997" strokeWidth={2} dot={false} />
          <Line yAxisId="right" type="monotone" dataKey="odo" name="YSI ODO (mg/L)" stroke="#a259e6" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

const ArrowButton = ({ dir, onClick }) => (
  <button
    onClick={onClick}
    aria-label={dir === 'prev' ? 'Previous slide' : 'Next slide'}
    className={`absolute top-[52px] ${dir === 'prev' ? 'left-3' : 'right-3'} z-20
                w-9 h-9 rounded-full bg-white/90 text-[#4D4D4D] shadow
                flex items-center justify-center active:bg-white`}
  >
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d={dir === 'prev' ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'}
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </button>
)

export function Hero() {
  const [slideIndex, setSlideIndex] = useState(0)
  const slide = HERO_SLIDES[slideIndex] || HERO_SLIDES[0]
  const multi = HERO_SLIDES.length > 1
  const go = (delta) => setSlideIndex((i) => (i + delta + HERO_SLIDES.length) % HERO_SLIDES.length)

  return (
    <div className="w-full">
      {/* ══ Mobile (< 768px) — XD mobile frame ══════════════════════════════
          Image with the title overlaid and carousel arrows, the chart card
          overlapping the foot of the image, then the body copy BELOW on
          white. The old layout stacked both paragraphs on top of the photo,
          where the text ran over the image and became unreadable. */}
      <div className="md:hidden">
        <div className="relative">
          <img
            src={slide.image}
            alt={slide.title}
            className="w-full h-[300px] object-cover"
          />
          <h1 className="absolute top-0 inset-x-0 pt-5 px-14 text-center text-white font-bold text-2xl leading-tight drop-shadow">
            {slide.title}
          </h1>
          {multi && (
            <>
              <ArrowButton dir="prev" onClick={() => go(-1)} />
              <ArrowButton dir="next" onClick={() => go(1)} />
            </>
          )}
        </div>

        {/* Chart card pulled up so it overlaps the image, as in the XD.
            Negative margin (rather than absolute positioning) keeps the
            following copy in normal flow, so it can never overlap. */}
        <div className="-mt-16 px-8 relative z-10">
          <HeroChart height={150} compact />
        </div>

        {/* Body copy — own block, white background */}
        <div className="bg-white px-8 pt-6 pb-8 space-y-4">
          {slide.paragraphs.map((text, i) => (
            <p key={i} className="text-[#4D4D4D] text-[15px] leading-relaxed">{text}</p>
          ))}
        </div>

        {multi && (
          <div className="flex justify-center gap-2 pb-6 bg-white">
            {HERO_SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => setSlideIndex(i)}
                aria-label={`Go to slide ${i + 1}`}
                aria-current={i === slideIndex}
                className={`w-2 h-2 rounded-full ${i === slideIndex ? 'bg-[#358ECD]' : 'bg-gray-300'}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* ══ Tablet + desktop (>= 768px) — unchanged ═════════════════════════ */}
      <div className="hidden md:block">
        {/* Hero Banner Section */}
        <div className="relative w-full" style={{ minHeight: '817px' }}>
          {/* Background Image */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: 'url("/images/banner.png")',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />

          {/* Content */}
          <div className="pt-cstm relative z-10 flex flex-col justify-center h-full px-4 sm:px-6 lg:px-[140px] pt-32 pb-40 ">
            <div className="max-w-[1920px] mx-auto w-full">
              <div className="max-w-2xl">
                {/* Main Title */}
                <h1 className="text-3xl banner-heading font-bold text-white mb-7 leading-tight">
                  Flood Warning System
                </h1>

                {/* Description Text */}
                <div className="max-w-2xl space-y-6">
                  <p className="bnr-line text-white text-xl md:text-xl leading[26px]">
                    Flash flooding often leaves only minutes to react before conditions approach dangerous levels. Flood warning systems save lives, protect property and limit damage to infrastructure by quickly alerting to heavy precipitation and changing water levels.
                  </p>
                  <p className="bnr-line text-white text-xl md:text-xl leading[26px]">
                    An effective flood warning system enables authorities to take appropriate action, such as evacuation and control of dams in advance of a flood event, often leading to much less catastrophic outcomes when severe weather strikes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Chart Card - Floating Section */}
        <div className="minuse-mt relative -mt-40 mb-16 px-4 sm:px-6 lg:px-[140px] z-20">
          <div className="max-w-[1920px] mx-auto">
            <div className="max-w-3xl">
              <HeroChart height={300} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
